import { useEffect } from 'react';
import { Keyboard } from '@capacitor/keyboard';
import { isNativePlatform } from '@/lib/platform';
import { error } from '@/utils/logger';
import {
  applyKeyboardInset,
  computeVisualViewportKeyboardInset,
  resetKeyboardInset,
} from '@/utils/keyboardInset';

/**
 * Tracks software keyboard height on native (Capacitor) and mobile web
 * (Visual Viewport API) and exposes it via `--keyboard-height` on the
 * document root plus `data-keyboard-open="true"` when inset > 0.
 *
 * Call once near the app root (e.g. RootLayout).
 */
export function useKeyboardInset(): void {
  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }

    const native = isNativePlatform();

    const syncWebInset = (): void => {
      if (native) return;
      applyKeyboardInset(computeVisualViewportKeyboardInset());
    };

    if (!native) {
      syncWebInset();
      const vv = window.visualViewport;
      vv?.addEventListener('resize', syncWebInset);
      vv?.addEventListener('scroll', syncWebInset);
      window.addEventListener('resize', syncWebInset);

      return () => {
        vv?.removeEventListener('resize', syncWebInset);
        vv?.removeEventListener('scroll', syncWebInset);
        window.removeEventListener('resize', syncWebInset);
        resetKeyboardInset();
      };
    }

    const showListener = Keyboard.addListener('keyboardWillShow', (info) => {
      applyKeyboardInset(info.keyboardHeight);
    });

    const hideListener = Keyboard.addListener('keyboardDidHide', () => {
      applyKeyboardInset(0);
    });

    return () => {
      showListener.then((h) => h.remove()).catch((e) => {
        error('[useKeyboardInset] Failed to remove show listener', e);
      });
      hideListener.then((h) => h.remove()).catch((e) => {
        error('[useKeyboardInset] Failed to remove hide listener', e);
      });
      resetKeyboardInset();
    };
  }, []);
}
